import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageCircle, Send, Users, Search, CheckCircle, XCircle, Loader2, AlertCircle, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Opt-in template SID
const OPTIN_TEMPLATE_SID = 'HXdc6a6d4717b5c3514e4571b8cf919de1';

const WhatsAppOptIn = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, sent: 0, failed: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Search params
  const [searchParams, setSearchParams] = useState({
    magazine: 'Aabkari Times',
    recipient_type: 'both',
    status: 'ACTIVE'
  });

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/whatsapp/recipients`, searchParams, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter only those with phone numbers
      const withPhone = response.data.filter(r => r.phone && r.phone.trim());
      setRecipients(withPhone);
      setSelectedRecipients(withPhone.map(r => r.id));
      toast.success(`Found ${withPhone.length} recipients with phone numbers`);
    } catch (error) {
      console.error('Error fetching recipients:', error);
      toast.error('Failed to fetch recipients');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRecipients(filteredRecipients.map(r => r.id));
    } else {
      setSelectedRecipients([]);
    }
  };

  const handleSelectRecipient = (id, checked) => {
    if (checked) {
      setSelectedRecipients([...selectedRecipients, id]);
    } else {
      setSelectedRecipients(selectedRecipients.filter(r => r !== id));
    }
  };

  const filteredRecipients = recipients.filter(r => 
    r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.phone?.includes(searchTerm) ||
    r.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSendOptIn = async () => {
    const selectedData = recipients.filter(r => selectedRecipients.includes(r.id));
    
    if (selectedData.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    setSending(true);
    setShowConfirm(false);
    setProgress({ current: 0, total: selectedData.length, sent: 0, failed: 0 });
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/whatsapp/send-optin-bulk`, {
        recipients: selectedData,
        template_sid: OPTIN_TEMPLATE_SID,
        campaign_name: `${searchParams.magazine} - WhatsApp Opt-in Request`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Opt-in request sent to ${response.data.sent_count} recipients!`);
      setProgress({
        current: response.data.total,
        total: response.data.total,
        sent: response.data.sent_count,
        failed: response.data.failed_count
      });
    } catch (error) {
      console.error('Error sending opt-in:', error);
      toast.error(error.response?.data?.detail || 'Failed to send opt-in requests');
    } finally {
      setSending(false);
    }
  };

  const handleTestMessage = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    
    setSendingTest(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/whatsapp/send-optin-test`, {
        phone: testPhone,
        template_sid: OPTIN_TEMPLATE_SID
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Test message sent! Check your WhatsApp.');
        setShowTestModal(false);
      }
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test message');
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Button variant="ghost" onClick={() => navigate('/')} className="mr-4">
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center">
                <MessageCircle className="mr-3 text-green-600" size={28} />
                WhatsApp Opt-in Request
              </h1>
              <p className="text-gray-600 mt-1">Send subscription request to all members & advertisers</p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex space-x-2">
            <Button variant="outline" onClick={() => setShowPreview(true)}>
              <Eye size={18} className="mr-2" />
              Preview
            </Button>
            <Button variant="outline" onClick={() => setShowTestModal(true)}>
              <MessageCircle size={18} className="mr-2" />
              Send Test
            </Button>
          </div>
        </div>

        {/* Magazine Selection */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex items-center space-x-4">
            <Label className="font-medium">Magazine:</Label>
            <select
              value={searchParams.magazine}
              onChange={(e) => setSearchParams({ ...searchParams, magazine: e.target.value })}
              className="h-10 rounded-md border px-3 text-sm font-medium"
            >
              <option value="Aabkari Times">Aabkari Times</option>
              <option value="Sugar Times" disabled>Sugar Times (Template not available)</option>
            </select>
            <span className="text-sm text-gray-500">
              This opt-in template is configured for Aabkari Times
            </span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Left - Message Preview */}
          <div className="col-span-1">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold mb-4">Message Preview</h2>
              
              <div className="bg-[#e5ddd5] rounded-lg p-4">
                <div className="bg-white rounded-lg p-3 shadow text-sm">
                  <p className="font-medium">Hello from Aabkari Times!</p>
                  <p className="mt-2">Exciting update: We're now delivering our exclusive monthly magazine directly to your WhatsApp — complete with stunning cover images and the full PDF edition.</p>
                  <p className="mt-2">Stay updated on the latest in excise policies, liquor industry news, ethanol, prohibition updates, and more — right in your chats!</p>
                  <p className="mt-2">To subscribe instantly, just reply: YES</p>
                  <p className="mt-2">This is a one-time confirmation — no further action needed.</p>
                  <p className="mt-2">Thank you for being part of our community!</p>
                  <p className="mt-2 font-medium">Team Aabkari Times</p>
                  <p className="text-gray-500">aabkaritimes.com | Prayagraj</p>
                  
                  <div className="mt-3 space-y-2">
                    <button className="w-full py-2 border rounded text-center text-sm font-medium hover:bg-gray-50">
                      YES, Subscribe
                    </button>
                    <button className="w-full py-2 border rounded text-center text-sm font-medium hover:bg-gray-50">
                      NO, Don't Subscribe
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  <CheckCircle size={16} className="inline mr-1" />
                  <strong>Template Approved</strong>
                </p>
                <p className="text-xs text-green-700 mt-1">
                  SID: {OPTIN_TEMPLATE_SID.slice(0, 20)}...
                </p>
              </div>
            </div>
          </div>

          {/* Right - Recipients */}
          <div className="col-span-2">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Users className="mr-2 text-green-600" size={20} />
                Select Recipients
              </h2>
              
              {/* Filters */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                <select
                  value={searchParams.recipient_type}
                  onChange={(e) => setSearchParams({ ...searchParams, recipient_type: e.target.value })}
                  className="h-9 rounded-md border px-2 text-sm"
                >
                  <option value="both">All</option>
                  <option value="members">Members Only</option>
                  <option value="advertisers">Advertisers Only</option>
                </select>
                <select
                  value={searchParams.status}
                  onChange={(e) => setSearchParams({ ...searchParams, status: e.target.value })}
                  className="h-9 rounded-md border px-2 text-sm"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="all">All Status</option>
                </select>
                <Button onClick={fetchRecipients} disabled={loading} className="col-span-2">
                  {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Search size={16} className="mr-2" />}
                  {loading ? 'Loading...' : 'Load Recipients'}
                </Button>
              </div>

              {/* Search */}
              {recipients.length > 0 && (
                <div className="mb-4">
                  <Input
                    placeholder="Search by name, phone, or company..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>
              )}

              {/* Recipients List */}
              {recipients.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-2 px-2">
                    <div className="flex items-center">
                      <Checkbox
                        checked={selectedRecipients.length === filteredRecipients.length && filteredRecipients.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                      <span className="ml-2 text-sm font-medium">
                        Select All ({filteredRecipients.length})
                      </span>
                    </div>
                    <span className="text-sm text-green-600 font-medium">
                      {selectedRecipients.length} selected
                    </span>
                  </div>
                  
                  <div className="border rounded-lg max-h-80 overflow-y-auto">
                    {filteredRecipients.slice(0, 100).map((recipient) => (
                      <div key={recipient.id} className="flex items-center p-2 border-b hover:bg-gray-50">
                        <Checkbox
                          checked={selectedRecipients.includes(recipient.id)}
                          onCheckedChange={(checked) => handleSelectRecipient(recipient.id, checked)}
                        />
                        <div className="ml-3 flex-1">
                          <p className="text-sm font-medium">{recipient.name}</p>
                          <p className="text-xs text-gray-500">
                            {recipient.phone} • {recipient.type === 'member' ? 'Member' : 'Advertiser'}
                            {recipient.company && ` • ${recipient.company}`}
                          </p>
                        </div>
                      </div>
                    ))}
                    {filteredRecipients.length > 100 && (
                      <p className="text-center text-xs text-gray-500 py-2">
                        +{filteredRecipients.length - 100} more recipients
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users size={48} className="mx-auto mb-2 opacity-50" />
                  <p>Click "Load Recipients" to fetch members & advertisers</p>
                </div>
              )}

              {/* Send Button */}
              {recipients.length > 0 && (
                <div className="mt-4">
                  <Button
                    onClick={() => setShowConfirm(true)}
                    className="w-full"
                    style={{ backgroundColor: '#25D366' }}
                    disabled={selectedRecipients.length === 0 || sending}
                    data-testid="send-optin-btn"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={18} />
                        Sending... ({progress.current}/{progress.total})
                      </>
                    ) : (
                      <>
                        <Send size={18} className="mr-2" />
                        Send Opt-in Request to {selectedRecipients.length} Recipients
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Progress Display */}
              {sending && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Progress: {progress.current}/{progress.total}</span>
                    <span className="text-green-600">Sent: {progress.sent}</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all" 
                      style={{ width: `${(progress.current / progress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Results */}
              {!sending && progress.total > 0 && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <h3 className="font-medium text-green-800 flex items-center">
                    <CheckCircle size={18} className="mr-2" />
                    Broadcast Complete
                  </h3>
                  <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
                    <div>
                      <p className="text-gray-600">Total</p>
                      <p className="font-bold text-lg">{progress.total}</p>
                    </div>
                    <div>
                      <p className="text-green-600">Sent</p>
                      <p className="font-bold text-lg text-green-600">{progress.sent}</p>
                    </div>
                    <div>
                      <p className="text-red-600">Failed</p>
                      <p className="font-bold text-lg text-red-600">{progress.failed}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <AlertCircle className="mr-2 text-amber-500" size={24} />
                Confirm Broadcast
              </h3>
              
              <p className="text-gray-600 mb-4">
                You are about to send the opt-in request to <strong>{selectedRecipients.length}</strong> recipients.
              </p>
              
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> This will use your Twilio WhatsApp credits. Each message costs approximately $0.005-0.05 depending on your plan.
                </p>
              </div>
              
              <div className="flex space-x-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  style={{ backgroundColor: '#25D366' }}
                  onClick={handleSendOptIn}
                >
                  <Send className="mr-2" size={18} />
                  Confirm & Send
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Eye className="mr-2 text-green-600" size={24} />
                Message Preview
              </h3>
              
              <div className="bg-[#e5ddd5] rounded-lg p-4 max-h-96 overflow-y-auto">
                <div className="bg-white rounded-lg p-4 shadow text-sm">
                  <p className="font-medium">Hello from Aabkari Times!</p>
                  <p className="mt-3">Exciting update: We're now delivering our exclusive monthly magazine directly to your WhatsApp — complete with stunning cover images and the full PDF edition.</p>
                  <p className="mt-3">Stay updated on the latest in excise policies, liquor industry news, ethanol, prohibition updates, and more — right in your chats!</p>
                  <p className="mt-3">To subscribe instantly, just reply: <strong>YES</strong></p>
                  <p className="mt-3">This is a one-time confirmation — no further action needed.</p>
                  <p className="mt-3">Thank you for being part of our community!</p>
                  <p className="mt-3 font-medium">Team Aabkari Times</p>
                  <p className="text-gray-500">aabkaritimes.com | Prayagraj</p>
                  
                  <div className="mt-4 space-y-2">
                    <button className="w-full py-2 bg-gray-100 rounded text-center text-sm font-medium hover:bg-gray-200">
                      YES, Subscribe
                    </button>
                    <button className="w-full py-2 bg-gray-100 rounded text-center text-sm font-medium hover:bg-gray-200">
                      NO, Don't Subscribe
                    </button>
                  </div>
                  
                  <p className="text-right text-xs text-gray-400 mt-3">
                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end mt-4">
                <Button onClick={() => setShowPreview(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Test Message Modal */}
        {showTestModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <MessageCircle className="mr-2 text-green-600" size={20} />
                Send Test Message
              </h3>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>WhatsApp Number *</Label>
                  <Input
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="+919876543210"
                    className="text-lg"
                    data-testid="test-phone-input"
                  />
                  <p className="text-xs text-gray-500">
                    Include country code (e.g., +91 for India)
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <p className="font-medium mb-1">This test will send:</p>
                  <p className="text-gray-600">
                    Opt-in request message with "YES, Subscribe" and "NO, Don't Subscribe" buttons
                  </p>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setShowTestModal(false)}>
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  style={{ backgroundColor: '#25D366' }}
                  onClick={handleTestMessage}
                  disabled={!testPhone || sendingTest}
                  data-testid="confirm-test-btn"
                >
                  {sendingTest ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={18} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={18} />
                      Send Test
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppOptIn;
